/**
 * Session & RBAC helpers.
 *
 * Reads the authenticated `user` (with `role.permissions`) that the auth flow
 * stores in localStorage (see useAuthService.setLoginDetails / getUserRole).
 * Centralizes permission checks so the layout, route guards, and pages all
 * gate on the same source of truth.
 */
import { User, UserPermissions, RoleName } from '../api/types';

const ADMIN_ROLES: RoleName[] = ['administrator', 'admin'];

export type PermissionKey = keyof UserPermissions;

/** The authenticated user object, or null if not logged in / unparseable. */
export function getUser(): User | null {
  const raw = localStorage.getItem('user');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export function getRole(): User['role'] {
  return getUser()?.role ?? null;
}

export function getPermissions(): UserPermissions | null {
  return getUser()?.role?.permissions ?? null;
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem('accessToken');
}

/** Administrator or Admin — kept for general admin-level checks. */
export function isAdmin(): boolean {
  const role = getRole()?.role_name;
  return !!role && ADMIN_ROLES.includes(role);
}

/**
 * Administrator only (the business creator). Billing/payments and Business
 * Details edit are restricted to this role — Admins and Providers are excluded.
 */
export function isAdministrator(): boolean {
  const role = getRole();
  return role?.role_name === 'administrator' || role?.is_creator === true;
}

/**
 * Roles allowed into the provider web console: Administrator, Admin, Provider.
 * Supervisors and Customers are blocked. A user with no role yet (a brand-new
 * provider mid-onboarding) is allowed through so onboarding can proceed.
 */
export function canAccessConsole(): boolean {
  const u = getUser();
  if (!u) return false;
  if (u.user_type === 'customer') return false;
  const role = u.role?.role_name;
  if (!role) return true;
  return role === 'administrator' || role === 'admin' || role === 'provider';
}

/**
 * Check a single permission flag. Defaults to `true` when permissions haven't
 * loaded yet (role fetch pending) so we don't flash-hide core nav; route
 * guards still protect the actual pages.
 */
export function can(key: PermissionKey): boolean {
  const perms = getPermissions();
  if (!perms) return true;
  // Use the backend flag when present...
  if (Object.prototype.hasOwnProperty.call(perms, key)) return !!perms[key];
  // ...but some deployed backends omit these two — derive from role to match
  // the backend's intended semantics (admins edit business; non-supervisors set holidays).
  if (key === 'can_edit_business') return isAdministrator();
  if (key === 'can_set_holidays') return getRole()?.role_name !== 'supervisor';
  return true;
}

/** Display name fallback chain for avatars / greetings. */
export function getDisplayName(): string {
  const u = getUser();
  if (!u) return 'User';
  return (
    u.display_name ||
    [u.first_name, u.last_name].filter(Boolean).join(' ') ||
    u.phone_number ||
    'User'
  );
}

export function getBusinessName(): string {
  return getRole()?.business_name || 'BaseQueue';
}

/**
 * Landing route after login. Providers (like the mobile app) have no Dashboard —
 * their home is the Queues list — so send them there; admins/administrators get
 * the Dashboard.
 */
export function landingPath(): string {
  return isAdmin() ? '/dashboard' : '/queues';
}

/**
 * Can manage queue settings (edit / duplicate / pause). Mirrors the mobile app:
 * Administrator, Admin and Provider can; only Supervisor cannot. (Creating a new
 * queue is separate and stays gated on the `can_manage_queues` permission.)
 */
export function canManageQueueSettings(): boolean {
  return getRole()?.role_name !== 'supervisor';
}

/** Clear all auth state. Caller handles navigation. */
export function clearSession(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
}

/** Two-letter initials for avatar placeholders. */
export function initials(name: string): string {
  return (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

/** Maps a role to its avatar gradient class key. */
export function roleGradientKey(role?: RoleName | null): 'admin' | 'provider' | 'supervisor' {
  if (role === 'provider') return 'provider';
  if (role === 'supervisor') return 'supervisor';
  return 'admin';
}
